import { Box, Typography, Button, Stack } from "@mui/material";
import { useState } from "react";
import { useQSOs } from "@/hooks/useQSOs";
import { useAddresses } from "@/hooks/useAddresses";
import { useDispatch, useSelector } from "react-redux";
import { showNotice, hideNotice, showError, hideError } from "@/store/slices/ui.slice";
import { selectNotice, selectError } from "@/store/selectors/ui.selectors";
import { AddressExcelService } from "@/services/excel/address-excel.service";
import { QSOExcelService } from "@/services/excel/qso-excel.service";
import { downloadBlob } from "@/utils/file.utils";
import Notice from "@/components/common/Notice";
import ErrorAlert from "@/components/common/ErrorAlert";
import ImportProgress from "@/components/common/ImportProgress";
import QSOTable from "@/components/tables/QSOTable";
import AddressFormDialog from "@/components/dialogs/AddressFormDialog";
import { AddressFormData } from "@/schemas/address.schema";

export default function PrintPage(): JSX.Element {
  const { qsos, loading, handleImport, handleClear, importProgress, duplicateCount } = useQSOs();
  const { addresses, handleCreate } = useAddresses();
  const notice = useSelector(selectNotice);
  const errorState = useSelector(selectError);
  const dispatch = useDispatch();
  const [importing, setImporting] = useState(false);
  const [importTotal, setImportTotal] = useState(0);
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [selectedCall, setSelectedCall] = useState("");

  const addressExcelService = new AddressExcelService();
  const qsoExcelService = new QSOExcelService();

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = event.target.files?.[0];
    if (!file) return;


    setImporting(true);
    setImportTotal(0);

    try {

      await handleImport(file);

      
      dispatch(showNotice("ADIF 导入成功"));
      if (duplicateCount > 0) {
        dispatch(showNotice(`发现 ${duplicateCount} 条重复 QSO`));
      }
    } catch {
      dispatch(showError("ADIF 导入失败"));
    } finally {

      setImporting(false);
      // 重置文件输入，这样即使用户选择同一个文件，onChange 事件也会触发
      event.target.value = '';
    }
  };

  const handleGenerateAddressExcel = async (): Promise<void> => {
    const files = await addressExcelService.generateAddressExcel(qsos);
    
    if (files.length === 0) {
      const uniqueCalls = [...new Set(qsos.map(qso => qso.call))];

      dispatch(showError(`没有找到匹配的收件人地址。当前有 ${qsos.length} 个 QSO，${uniqueCalls.length} 个唯一呼号。请检查地址管理中的地址状态是否为"有效"，或添加缺失的地址。`));
      return;
    }
    

    files.forEach((file) => {
      const blob = new Blob([Uint8Array.from(file.buffer)], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      downloadBlob(blob, file.filename);
    });
    dispatch(showNotice(`收件人地址 Excel 生成成功，共 ${files.length} 个文件`));
  };

  const handleGenerateQSOExcel = (): void => {
    const validAddresses = addresses.filter(addr => addr.status === "valid" && addr.address.trim() !== "");
    const validCalls = new Set(validAddresses.map(addr => addr.call));
    const filteredQSOs = qsos.filter(qso => validCalls.has(qso.call));

    if (filteredQSOs.length === 0) {
      dispatch(showError("没有找到有地址的 QSO，无法生成 QSO 标签 Excel"));
      return;
    }

    const files = qsoExcelService.generateQSOExcel(filteredQSOs);
    files.forEach((file) => {
      const blob = new Blob([Uint8Array.from(file.buffer)], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      downloadBlob(blob, file.filename);
    });
    dispatch(showNotice("QSO 标签 Excel 生成成功"));
  };

  const handleQRZClick = (call: string): void => {
    const url = `https://www.qrz.com/db/${call}`;
    window.open(url, "_blank");
  };

  const handleAddAddress = (call: string): void => {
    setSelectedCall(call);
    setAddressDialogOpen(true);
  };

  const handleAddressSubmit = async (data: AddressFormData): Promise<void> => {
    try {
      await handleCreate({
        call: data.call,
        address: data.address,
        zip: data.zip || "",
        status: data.status,
        outsea: data.outsea,
      });
      dispatch(showNotice("地址添加成功"));
    } catch (error) {
      dispatch(showError(error instanceof Error ? error.message : "地址添加失败"));
      throw error;
    }
  };

  const handleCloseAddressDialog = (): void => {
    setAddressDialogOpen(false);
    setSelectedCall("");
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        打印页面
      </Typography>
      {notice && <Notice message={notice} onClose={() => dispatch(hideNotice())} />}
      {errorState && <ErrorAlert message={errorState} onClose={() => dispatch(hideError())} />}
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <Button variant="contained" component="label" disabled={importing}>
          导入 ADIF
          <input
            type="file"
            accept=".adif,.adi"
            hidden
            onChange={(e) => {
              void handleFileSelect(e);
            }}
          />
        </Button>
        <Button variant="outlined" onClick={() => {
          void (async (): Promise<void> => {
            try {
              await handleClear();
            } catch {
              dispatch(showError("清空 QSO 失败"));
            }
          })();
        }} disabled={qsos.length === 0}>
          清空 QSO
        </Button>
      </Stack>
      {importing && (
        <ImportProgress
          progress={importProgress}
          total={importTotal}
          onCancel={() => setImporting(false)}
        />
      )}
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <Button
          variant="contained"
          onClick={() => void handleGenerateAddressExcel()}
          disabled={qsos.length === 0}
        >
          生成收件人地址 Excel
        </Button>
        <Button variant="contained" onClick={handleGenerateQSOExcel} disabled={qsos.length === 0}>
          生成 QSO 标签 Excel
        </Button>
      </Stack>
      <QSOTable qsos={qsos} loading={loading} onQRZClick={handleQRZClick} addresses={addresses} onAddAddress={handleAddAddress} />
      <AddressFormDialog
        open={addressDialogOpen}
        mode="add"
        initialCall={selectedCall}
        onClose={handleCloseAddressDialog}
        onSubmit={handleAddressSubmit}
      />
    </Box>
  );
}
